<template>
  <div>
    <v-text-field
      v-model="facilitiesSearchInputValue"
      :placeholder="
        $t('message.businessAddresses.groups.facilities.filterInputPlaceholder')
      "
      prepend-inner-icon="icon-search"
      clearable
      outlined
      rounded
      class="mb-4"
      :hide-details="true"
      @input="onFacilitiesSearch"
    ></v-text-field>
    <v-row no-gutters class="align-center mb-3">
      <v-col>
        <Pagination
          class="flex-grow-1"
          :page="page"
          :per-page="perPage"
          :options="perPageOptions"
          :total-count="totalCount"
          :disabled="loading"
          @change="handleChange"
        >
          <div class="post-facilities-dialog__positions-count">
            {{ $t("message.common.positionsCount", [totalCount]) }}
          </div>
        </Pagination>
      </v-col>
    </v-row>

    <div v-if="loaded && facilities.length" class="position-relative">
      <v-list>
        <v-list-item-group active-class="">
          <v-virtual-scroll
            class="list"
            :items="facilities"
            :item-height="listItemHeight"
            :height="listHeight"
            ref="list"
          >
            <template v-slot:default="{ item }">
              <v-radio-group
                v-model="selectedItem"
                hide-details
                class="radio-group"
              >
                <v-list-item
                  :key="item.id"
                  :value="item.id"
                  :ripple="false"
                  class="list-item"
                  @click="selectedItem = item.id"
                >
                  <template v-slot:default>
                    <v-list-item-action class="list-item__action">
                      <v-radio :value="item.id" hide-details></v-radio>
                    </v-list-item-action>
                    <v-list-item-content class="d-flex flex-col">
                      <div class="d-flex align-center">
                        <IsOwnerIndicator :isOwner="true" class="mr-3" />
                        <div class="list-item__content-primary">
                          <p class="list-item__name">{{ item.name }}</p>
                          <div class="list-item__description">
                            <p class="list-item__address">
                              {{ item.formatted_address }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-radio-group>
            </template>
          </v-virtual-scroll>
        </v-list-item-group>
      </v-list>

      <SectionLoader :status="loading" :opacity="0.75" />
    </div>
    <div v-else class="no-data">
      <p class="mb-0">
        {{ $t("message.posts.facilitiesToAddDialog.noFacilities") }}
      </p>
      <SectionLoader :status="loading" :opacity="1" />
    </div>

    <div class="action-bar">
      <ui-button color="primary" outlined @click="handleClose">
        {{ $t("message.common.cancel") }}
      </ui-button>
      <ui-button
        color="primary"
        :disabled="!selectedItem"
        @click="handleNextStep"
      >
        {{ $t("message.common.further") }}
      </ui-button>
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/List/Pagination";
import SectionLoader from "@/components/Loader/SectionLoader";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";

import { debounce, scrollToTop } from "@/helpers/helpers";
import serviceHandler from "@/helpers/handlers/service";
import facilitiesService from "@/services/facilities";

export default {
  name: "QaManageDialogLocationListView",
  components: { Pagination, SectionLoader, IsOwnerIndicator },

  data() {
    return {
      loaded: false,
      loading: false,
      facilitiesSearchInputValue: "",
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      facilities: [],
      listItemHeight: 65,
      selectedItem: null
    };
  },
  computed: {
    listHeight() {
      const maxListItems = 7;

      if (this.facilities.length > maxListItems) {
        return this.listItemHeight * maxListItems;
      } else if (this.facilities.length < 2) {
        return this.listItemHeight * 2;
      } else {
        return this.listItemHeight * this.facilities.length + 1; // 1px added to avoid scrollbar show up
      }
    }
  },
  async created() {
    await this.initializeData();
  },
  methods: {
    async initializeData() {
      this.loading = true;
      await this.fetchFacilities();
      this.loading = false;
      this.loaded = true;
    },
    async fetchFacilities() {
      const params = {
        page: this.page,
        perPage: this.perPage,
        query: this.facilitiesSearchInputValue,
        is_owner: true
      };

      const response = await serviceHandler(
        facilitiesService.getFacilitiesData(params)
      );

      if (response && "results" in response && "count" in response) {
        this.facilities = [...response.results];
        this.totalCount = response.count;
      }
    },

    onFacilitiesSearch: debounce(async function() {
      this.loading = true;
      this.page = 1;
      await this.fetchFacilities();
      this.loading = false;
    }, 500),

    async handleChange({ page, perPage }) {
      this.$nextTick(() => {
        scrollToTop(this.$refs.list.$el);
      });
      this.loading = true;
      this.page = page;
      this.perPage = perPage;
      await this.fetchFacilities();
      this.loading = false;
    },

    handleClose() {
      this.$emit("close");
    },
    handleNextStep() {
      const selectedFacility = this.facilities.find(
        facility => facility.id === this.selectedItem
      );
      const { id, name, formatted_address, store_code } = selectedFacility;
      this.$emit("next-step", {
        id,
        name,
        formatted_address,
        store_code
      });
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-list {
    padding: 0;
  }

  .v-input--radio-group__input {
    height: 65px;
  }

  .radio-group {
    padding-top: 0;
    margin-top: 0;
  }
}
.list {
  border-top: 1px solid $light-3;
  position: relative;
}

.list-item {
  border-bottom: 1px solid $light-3;
  height: 65px !important;

  &__action {
    margin-right: $spacing * 2 !important;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__name {
    @include font-size(14px);
    color: $text;
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__description {
    position: relative;
    display: flex;
    @include font-size(12px);
    font-weight: 400;
    margin-bottom: 0;
    max-width: 100%;
    white-space: nowrap;
    color: $text;

    & > p {
      margin-bottom: 0;
    }
  }
}

.action-bar {
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  padding: 24px 0;
}

.no-data {
  position: relative;
  @include noData(100px);
}
</style>

<style lang="scss">
.post-facilities-dialog__positions-count {
  color: $text;
  font-weight: 400;
  @include font-size(12px);
}
</style>
